import { Box } from '@mui/material';
import dayjs from 'dayjs';
import { SGRStateFilter } from 'lib/consts';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import riskPremiumCalculator from 'lib/functions/riskPremiumCalculator';
import { formatCurrency, showAlert } from 'lib/functions/utils';
import { Document, Warranty } from 'lib/types/models';
import { Pagination } from 'lib/types/shared';
import React from 'react';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

const SHOW_INVOICING_STATES = ['active', 'claimed', 'cancelled'];

type FetchWarranties = (params?: {
  page: number;
  pageSize: number;
  exportComplete?: boolean;
  pruffConnect?: boolean;
}) => Promise<void>;

interface WarrantiesTableProps {
  warranties: Warranty[];
  setWarranties: (warranties: Warranty[]) => void;
  handleOpenWarrantyDetail: (warrantyId: number) => void;
  pagination: Pagination;
  setPagination: (pagination: Pagination) => void;
  loading: boolean;
  fetchWarranties: FetchWarranties;
  setIsLoading: (isLoading: boolean) => void;
  buttons?: CustomButtonProps[];
  stateFilter?: SGRStateFilter;
  stateFilterComponent?: React.ReactNode;
  showBusiness?: boolean;
  pruffConnect?: boolean;
  exportComplete?: boolean;
}

const WarrantiesTable = ({
  warranties,
  setWarranties,
  handleOpenWarrantyDetail,
  pagination,
  setPagination,
  fetchWarranties,
  loading,
  setIsLoading,
  buttons,
  stateFilter,
  stateFilterComponent,
  showBusiness = true,
  pruffConnect = false,
  exportComplete = false,
}: WarrantiesTableProps) => {
  const dispatch = useDispatch();
  const { calculate } = riskPremiumCalculator();

  const quotedAmount = (value: number, row: Warranty) => {
    return formatCurrency(value, row.quotedCurrency as 'CLP' | 'USD' | 'UF');
  };

  const calculatePremium = (
    warranty: Warranty & { documents?: Document[] },
  ) => {
    if (warranty && warranty.documents && warranty.documents?.length > 0) {
      const value = warranty.documents?.[0]?.amount;
      return formatCurrency(value, 'CLP');
    }
    try {
      const value = calculate({
        startDate: dayjs(warranty.startDate),
        endDate: dayjs(warranty.endDate),
        quotedAmount: warranty.quotedAmount,
        quotedCurrency: warranty.quotedCurrency,
        rates: {
          variableRate: warranty.operationCostVariable ?? null,
          fixedRate: warranty.operationCostFixed ?? null,
          variableDiscount: warranty.discountRateVariable ?? null,
          fixedDiscount: warranty.discountRateFixed ?? null,
        },
      });
      return formatCurrency(value?.amount, 'CLP');
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Error al calcular el costo de emisión');
      return formatCurrency(0, 'CLP');
    }
  };

  const dateHeader = useMemo(() => {
    switch (stateFilter) {
      case 'active':
        return { label: 'Fecha Emisión', key: 'issuedDate', type: 'date' };
      case 'claimed':
        return { label: 'Fecha Siniestro', key: 'claimDate', type: 'date' };
      case 'lost':
        return { label: 'Fecha Pérdida', key: 'lostDate', type: 'date' };
      case 'cancelled':
        return { label: 'Fecha Anulación', key: 'cancelDate', type: 'date' };
      default:
        return {
          label: 'Fecha Actualización',
          key: 'updatedAt',
          type: 'date',
        };
    }
  }, [stateFilter]);

  const showInvoicing = SHOW_INVOICING_STATES.includes(stateFilter as string);

  const tableHeaders = [
    {
      label: 'ID',
      key: 'displayId',
      type: 'text',
    },
    {
      label: 'ID Licitación',
      key: 'tenderId',
      type: 'text',
    },
    {
      label: 'Finalidad',
      key: 'translatedType',
    },
    ...(showBusiness
      ? [
          {
            label: 'Empresa',
            key: 'business',
            type: 'dual-text',
            sortKey: 'business.primary',
          },
        ]
      : []),

    {
      label: 'Mandante',
      key: 'principal',
      type: 'dual-text',
      sortKey: 'principal.primary',
    },
    {
      label: 'Monto Cotizado',
      key: 'quotedAmount',
      customComponent: quotedAmount,
    },
    {
      label: 'Costo de emisión',
      key: 'premium',
      type: 'money',
    },
    dateHeader,
    {
      label: 'Fecha Inicio',
      key: 'startDate',
      type: 'date',
    },
    {
      label: 'Fecha Término',
      key: 'endDate',
      type: 'date',
    },
    {
      label: 'Estado',
      key: 'translatedStatus',
      type: 'status-chip',
    },
    ...(showInvoicing
      ? [
          {
            label: 'Factura',
            key: 'invoiceStatus',
            type: 'status-chip',
          },
        ]
      : []),
    {
      label: 'Ejecutivo',
      key: 'executive',
      type: 'text',
    },
  ];

  const pruffConnectHeaders = [
    {
      label: 'ID',
      key: 'displayId',
      type: 'text',
    },
    {
      label: 'ID Licitación',
      key: 'tenderId',
      type: 'text',
    },
    {
      label: 'Finalidad',
      key: 'translatedType',
    },
    {
      label: 'Mandante',
      key: 'principal',
      type: 'dual-text',
      sortKey: 'principal.primary',
    },
    {
      label: 'Monto Cotizado',
      key: 'quotedAmount',
      customComponent: quotedAmount,
    },
    {
      label: 'Costo de emisión',
      key: 'premium',
      type: 'money',
    },
    dateHeader,
    {
      label: 'Fecha Inicio',
      key: 'startDate',
      type: 'date',
    },
    {
      label: 'Fecha Término',
      key: 'endDate',
      type: 'date',
    },
    {
      label: 'Estado',
      key: 'translatedStatus',
      type: 'status-chip',
    },
    ...(showInvoicing
      ? [
          {
            label: 'Factura',
            key: 'invoiceStatus',
            type: 'status-chip',
          },
        ]
      : []),
  ];

  const headers = pruffConnect ? pruffConnectHeaders : tableHeaders;

  const warrantiesWithPremium = warranties.map((warranty) => ({
    ...warranty,
    premium: calculatePremium(warranty),
  }));

  return (
    <Box>
      <HeaderWithActions
        variant="body1"
        buttons={buttons}
        customTitle={stateFilterComponent}
      />
      <TableWithPagination
        data={warrantiesWithPremium}
        setData={setWarranties}
        dataHeaders={headers}
        handleClickRow={handleOpenWarrantyDetail}
        showActions
        loading={loading}
        pagination={pagination}
        setPagination={setPagination}
        searchData={fetchWarranties}
        setLoading={setIsLoading}
        exportToFile="xlsx"
        exportComplete={exportComplete}
        lastElement
      />
    </Box>
  );
};

export default WarrantiesTable;
